import {
  Button,
  CloseButton,
  Grid,
  Heading,
  IconAddLine,
  IconButton,
  IconTrashLine,
  Modal,
  Spinner,
  TextInput,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { MasteryLevelService } from "../../../services/MasteryLevelService";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../DialogYesNo";

const ConfigureMasteryLevelsModal = ({
  open,
  setOpen,
  handleClose,
  masteryLevels,
  setUpdate,
}) => {
  const showNotification = useNotification();
  const [loading, isLoading] = useState(false);
  const [updatedLevels, setUpdatedLevels] = useState([...masteryLevels]);
  const [openConfirm, setOpenConfirm] = useState(false);

  useEffect(() => {
    setUpdatedLevels([...masteryLevels]);
  }, [masteryLevels]);

  const handleSubmit = () => {
    setOpenConfirm(false);
    isLoading(true);
    MasteryLevelService.createOrUpdateMasteryLevels(updatedLevels)
      .then(() => {
        setUpdate(true);
        isLoading(false);
        setOpen(false);
        showNotification(t("notifications.masteryLevels.configSuccess"));
      })
      .catch((e) => {
        showNotification(
          `${t("notifications.masteryLevels.configError")} ${e}`,
          ERROR
        );
      });
  };

  const handleAdd = () => {
    setUpdatedLevels([
      ...updatedLevels,
      { seq: "", level: "", description: "" },
    ]);
  };

  const handleRemove = (index) => {
    const newArr = [...updatedLevels];
    newArr.splice(index, 1);
    setUpdatedLevels(newArr);
  };

  return (
    <Modal
      open={open}
      size="large"
      label={t("masteryLevels.configMasteryLevels")}
      onExited={() => setUpdatedLevels([...masteryLevels])}
    >
      <Modal.Header>
        <Grid hAlign="space-between">
          <Grid.Row>
            <Grid.Col>
              <Heading level="h2" as="h1">
                {t("masteryLevels.configMasteryLevels")}
              </Heading>
            </Grid.Col>
            <Grid.Col width="auto">
              <CloseButton
                size="small"
                screenReaderLabel={t("global.close")}
                onClick={() => handleClose()}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </Modal.Header>
      <Modal.Body>
        <Grid colSpacing="large" rowSpacing="medium" vAlign="bottom">
          {updatedLevels?.map((level, key) => (
            <Grid.Row key={key}>
              <Grid.Col width={2}>
                <TextInput
                  id={`mastery-level-seq-${key}`}
                  renderLabel={t(`masteryLevels.masteryLevelSeq`)}
                  align-items="center"
                  value={level ? `${level.seq}` : ""}
                  onChange={(e) => {
                    setUpdatedLevels([
                      ...updatedLevels.slice(0, key),
                      {
                        ...updatedLevels[key],
                        seq: isNaN(parseInt(e.currentTarget.value))
                          ? 0
                          : parseInt(e.currentTarget.value),
                      },
                      ...updatedLevels.slice(key + 1),
                    ]);
                  }}
                />
              </Grid.Col>
              <Grid.Col width={3}>
                <TextInput
                  id={`mastery-level-${key}`}
                  renderLabel={t(`masteryLevels.masteryLevel`)}
                  value={level ? level.level : ""}
                  onChange={(e) => {
                    setUpdatedLevels([
                      ...updatedLevels.slice(0, key),
                      { ...updatedLevels[key], level: e.currentTarget.value },
                      ...updatedLevels.slice(key + 1),
                    ]);
                  }}
                />
              </Grid.Col>
              <Grid.Col>
                <TextInput
                  id={`mastery-levels-description-${key}`}
                  renderLabel={t(`masteryLevels.masteryLevelDescription`)}
                  value={level ? level.description : ""}
                  onChange={(e) => {
                    setUpdatedLevels([
                      ...updatedLevels.slice(0, key),
                      {
                        ...updatedLevels[key],
                        description: e.currentTarget.value,
                      },
                      ...updatedLevels.slice(key + 1),
                    ]);
                  }}
                />
              </Grid.Col>
              <Grid.Col width={1}>
                <IconButton
                  onClick={() => handleRemove(key)}
                  renderIcon={IconTrashLine}
                  screenReaderLabel={t("masteryLevels.removeLevel")}
                />
              </Grid.Col>
            </Grid.Row>
          ))}
          <Grid.Row>
            <Grid.Col>
              <Button onClick={handleAdd} renderIcon={IconAddLine}>
                {t("masteryLevels.addLevel")}
              </Button>
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </Modal.Body>
      <Modal.Footer>
        <Button margin="0 small" onClick={() => handleClose()}>
          {t("global.cancel")}
        </Button>
        <Button
          color="primary"
          type="submit"
          onClick={() => setOpenConfirm(true)}
          disabled={loading}
        >
          {loading ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            t("global.submit")
          )}
        </Button>
        <DialogYesNo
          message={t("masteryLevels.masteryConfigWarning")}
          openStatus={openConfirm}
          onClickNo={() => setOpenConfirm(false)}
          onClickYes={handleSubmit}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default ConfigureMasteryLevelsModal;
